import { useEffect, useState } from 'react';
import { Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ShareSheet } from '@/components/common/ShareSheet';
import { loadedParcels } from '@/components/maps/ParcelOverlay';
import { parcelAtPoint, getParcelRef } from '@/utils/parcelGeometry';
import { getParcelMembersByRef } from '@/api/parcels';
import { getMember } from '@/api/fpaMembers';
import { buildMemberSharePayload } from '@/utils/memberShare';
import { parcelRecordCentroid } from '@/utils/memberParcelCentroid';
import type { ParcelRecord } from '@/utils/parcelGeometry';
import type { SharePayload } from '@/utils/share';

interface PinPropertyActionProps {
  /** Where the user dropped the pin. */
  pin: [number, number];
  /**
   * Only offered while the parcels layer is on — the lookup runs against the
   * geometry that layer has loaded, so with it off there is nothing to test
   * against and the button would be a dead end.
   */
  enabled: boolean;
}

/**
 * A row from `GET /parcels/by-ref/{ref}/members`. The fields are `member_id` /
 * `member_name` — the link table denormalises them onto itself — not `id` /
 * `name`. Reading it as a member record is what previously sent `getMember`
 * an undefined id.
 */
interface ParcelOwnerRecord {
  member_id: number;
  member_name?: string | null;
  member_ref?: string | null;
}

/**
 * "Share property" for a pin that landed on a member's parcel.
 *
 * Resolution is entirely client-side: the parcels overlay has already fetched
 * and cached the geometry it drew, so this reuses it rather than adding a
 * point-in-parcel endpoint. Renders nothing at all when the pin is on open
 * ground, so the pin's own actions stay uncluttered in the common case.
 *
 * A parcel with co-owners shares the first one, matching how the parcel
 * drawer leads with a single name.
 */
export function PinPropertyAction({ pin, enabled }: PinPropertyActionProps) {
  const [owner, setOwner] = useState<ParcelOwnerRecord | null>(null);
  // Kept alongside the owner so the share can use the parcel's own centroid
  // rather than re-resolving the link it was just found through.
  const [parcel, setParcel] = useState<ParcelRecord | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [payload, setPayload] = useState<SharePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve pin -> parcel -> member whenever the pin moves.
  useEffect(() => {
    if (!enabled) {
      setOwner(null);
      setParcel(null);
      return undefined;
    }
    const hit = parcelAtPoint(pin, loadedParcels());
    const ref = hit ? getParcelRef(hit) : null;
    if (!ref) {
      setOwner(null);
      setParcel(null);
      return undefined;
    }

    let cancelled = false;
    getParcelMembersByRef(ref)
      .then((data) => {
        if (cancelled) return;
        // The payload key is `owners`. Reading `members` here silently
        // disabled this button for every role, ADMIN included — the empty
        // array looked exactly like "this parcel has no member".
        const owners = data?.owners ?? [];
        setOwner(owners[0] ?? null);
        setParcel(hit);
      })
      .catch(() => {
        // Non-fatal — a view-only role gets no member back, which correctly
        // leaves the button hidden rather than showing a broken action.
        if (!cancelled) {
          setOwner(null);
          setParcel(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pin, enabled]);

  if (!owner) return null;

  async function openShare() {
    if (!owner) return;
    setPayload(null);
    setError(null);
    setLoading(true);
    setShareOpen(true);
    try {
      const full = await getMember(owner.member_id);
      setPayload(buildMemberSharePayload(full, parcelRecordCentroid(parcel)));
    } catch (err) {
      console.error('Failed to load member for sharing:', err);
      setError('Could not load this property. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={openShare} className="gap-1.5">
        <Home className="h-3.5 w-3.5" />
        Share property
      </Button>

      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        payload={payload}
        loading={loading}
        error={error}
      />
    </>
  );
}

export default PinPropertyAction;
